import { Button, Card } from '../../../design/components'
import { useSceneSession, useSceneSessionStore } from '../sessionState'
import styles from './play.module.css'

/**
 * Design spec §5.3's exchange counter, and its "End Exchange" control.
 *
 * **This number is device-local and the card says so.** The API has no exchange, round
 * or turn concept at all (implementation plan §3), so there is nothing to persist it to
 * and nothing to share it through. The alternative to saying that plainly is a counter
 * that looks shared, which at a table means two phones quietly disagreeing about which
 * exchange it is — a worse failure than an honest label.
 *
 * It lives in `sessionState.ts` and nowhere else, so a future `SceneState` resource is
 * one module's worth of change.
 */
export function ExchangeCard({ sceneId }: { sceneId: number }) {
  const session = useSceneSession(sceneId)
  const endExchange = useSceneSessionStore((state) => state.endExchange)
  const resetExchange = useSceneSessionStore((state) => state.resetExchange)

  return (
    <Card variant="fate" label="Exchange" hint="On this device">
      <div className={styles.exchange}>
        <span className={styles.exchangeValue}>{session.exchange}</span>
        <span className={styles.exchangeBody}>
          <span className={styles.deviceLocal}>Not shared with the table</span>
          <span className={styles.rowText}>
            The API has no exchange counter, so this one lives in this tab and clears when it
            closes.
          </span>
        </span>
      </div>

      <div className={styles.actions}>
        <Button onClick={() => endExchange(sceneId)}>End exchange</Button>
        <Button
          variant="secondary"
          disabled={session.exchange === 1}
          onClick={() => resetExchange(sceneId)}
        >
          Reset
        </Button>
      </div>
    </Card>
  )
}
